<script setup lang="ts">
import {
  instanceOfEnumerationValueListDto,
  instanceOfNumericDataSetFilterDto,
  instanceOfTextValueListDto,
} from '@/api/generator';
import SearchField from '@/components/filter/SearchField.vue';
import DatePicker from '@/components/forms/DatePicker/DatePicker.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import ShowMore from '@/components/ShowMore/ShowMore.vue';
import { I18nKey } from '@/utils/typings.ts';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import {
  isOptionSelected,
  useDataSetFiltering,
} from '@/views/TrendsSelectionView/useDataSetFiltering.ts';
import { mdiCheckboxBlankOutline, mdiCheckboxMarkedOutline } from '@mdi/js';
import { useElementBounding, useToggle, useWindowSize } from '@vueuse/core';
import { computed, useTemplateRef, watch } from 'vue';
import { TextValueListDtoWithId } from '../types.ts';
import { clear } from '@/components/icons/icons';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import Button from '@/components/buttons/Button.vue';

const [isFilterMenuOpen, toggleFilterMenu] = useToggle(false);

defineProps<{ searchFieldPlaceholderText: string }>();

const emit = defineEmits<{
  filtersChanged: [];
}>();

const {
  searchFieldInput,
  startDate,
  endDate,
  filterMenuEntries,
  searchFilter,
  selectTaskType,
  deselectTaskType,
  isTaskTypeSelected,
  selectSupportSystem,
  deselectSupportSystem,
  isSupportSystemSelected,
  selectTargetSystem,
  deselectTargetSystem,
  isTargetSystemSelected,
  selectEvaluationParameter,
  deselectEvaluationParameter,
  isEvaluationParameterSelected,
  toggleParameterOption,
  resetSelectedFilters,
  hasActiveFilters,
  hasChangedDates,
  resetDates,
} = useDataSetFiltering();

const searchField = useTemplateRef<HTMLElement | undefined>('searchField');

const searchFieldBounding = useElementBounding(searchField);
const windowSize = useWindowSize();

const dimensions = computed(() => {
  return {
    maxWidth: searchFieldBounding.width.value,
    maxHeight: windowSize.height.value - searchFieldBounding.bottom.value - 48,
  };
});

const isStartDateEarlierThanEndDate = computed(
  () => startDate.value && endDate.value && startDate.value.toMillis() < endDate.value.toMillis(),
);
const isEndDateLaterThanStartDate = computed(
  () => startDate.value && endDate.value && endDate.value.toMillis() > startDate.value.toMillis(),
);

watch(searchFilter, () => {
  emit('filtersChanged');
});

const clearFiltersAndDatesHandler = () => {
  resetSelectedFilters();
  resetDates();
};
</script>

<template>
  <v-menu
    id="filter-menu"
    v-model="isFilterMenuOpen"
    v-bind="dimensions"
    location="bottom left"
    locationStrategy="connected"
    :openOnClick="false"
    :openOnFocus="false"
    :closeOnContentClick="false"
    scrollStrategy="block"
  >
    <template #activator="{ props }">
      <div id="filter-controls">
        <div ref="searchField" class="w-100">
          <SearchField
            id="search-field"
            v-model="searchFieldInput"
            :placeholder="searchFieldPlaceholderText"
            v-bind="props"
            :label="$t('Filter')"
            class="input w-100"
            @focus="toggleFilterMenu(true)"
            @keydown="toggleFilterMenu(true)"
          />
        </div>

        <div class="right">
          <DatePicker
            v-model="startDate"
            :label="$t('Start')"
            :errorMessage="
              startDate
                ? endDate
                  ? isStartDateEarlierThanEndDate
                    ? undefined
                    : $t('TheStartDateCannotBeAfterTheEndDate')
                  : undefined
                : $t('Validation.PropertyRequired')
            "
          />
          <DatePicker
            v-model="endDate"
            :label="$t('End')"
            :errorMessage="
              endDate
                ? startDate
                  ? isEndDateLaterThanStartDate
                    ? undefined
                    : $t('TheEndDateCannotBeBeforeTheStartDate')
                  : undefined
                : $t('Validation.PropertyRequired')
            "
          />

          <Tooltip>
            <template #target="{ props: tooltipProps }">
              <Button
                v-bind="{ ...tooltipProps }"
                variant="ghost"
                size="small"
                class="mt-7"
                :icon="clear"
                iconPosition="start"
                :disabled="!hasActiveFilters && !hasChangedDates"
                @click="clearFiltersAndDatesHandler"
              />
            </template>
            <template #tooltip> {{ $t('ClearFilters') }} </template>
          </Tooltip>
        </div>
      </div>
    </template>

    <v-list>
      <v-list-item>
        <!-- Task Types -->
        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('TaskType_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <div
              v-if="filterMenuEntries.taskTypes.length > 0"
              class="select-group"
              @update:modelValue="() => {}"
            >
              <template v-for="taskType in filterMenuEntries.taskTypes" :key="taskType">
                <div class="select-item">
                  <Button
                    v-if="isTaskTypeSelected(taskType)"
                    variant="primary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxMarkedOutline"
                    @click="
                      () => {
                        deselectTaskType(taskType);
                      }
                    "
                  >
                    {{ $t(`TaskTypeDescriptionEnum.${taskType}`) }}
                  </Button>
                  <Button
                    v-else
                    variant="secondary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxBlankOutline"
                    @click="
                      () => {
                        selectTaskType(taskType);
                      }
                    "
                  >
                    {{ $t(`TaskTypeDescriptionEnum.${taskType}`) }}
                  </Button>
                </div>
              </template>
            </div>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Support Systems -->
        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('SupportSystem_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <div v-if="filterMenuEntries.supportSystems.length > 0" class="select-group">
              <template
                v-for="supportSystem in filterMenuEntries.supportSystems"
                :key="supportSystem.value"
              >
                <div class="select-item">
                  <Button
                    v-if="isSupportSystemSelected(supportSystem.id)"
                    variant="primary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxMarkedOutline"
                    @click="
                      () => {
                        deselectSupportSystem(supportSystem.id);
                      }
                    "
                  >
                    <IdentifierLabel :label="supportSystem.name" :identifier="supportSystem.iuid" />
                  </Button>
                  <Button
                    v-else
                    variant="secondary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxBlankOutline"
                    @click="
                      () => {
                        selectSupportSystem(supportSystem.id);
                      }
                    "
                  >
                    <IdentifierLabel :label="supportSystem.name" :identifier="supportSystem.iuid" />
                  </Button>
                </div>
              </template>
            </div>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Target Systems -->
        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('MedicalSystem_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <div v-if="filterMenuEntries.targetSystems.length > 0" class="select-group">
              <template
                v-for="targetSystem in filterMenuEntries.targetSystems"
                :key="targetSystem.value"
              >
                <div class="select-item">
                  <Button
                    v-if="isTargetSystemSelected(targetSystem.id)"
                    variant="primary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxMarkedOutline"
                    @click="
                      () => {
                        deselectTargetSystem(targetSystem.id);
                      }
                    "
                  >
                    <IdentifierLabel :label="targetSystem.name" :identifier="targetSystem.iuid" />
                  </Button>
                  <Button
                    v-else
                    variant="secondary"
                    block
                    size="x-small"
                    iconPosition="start"
                    :icon="mdiCheckboxBlankOutline"
                    @click="
                      () => {
                        selectTargetSystem(targetSystem.id);
                      }
                    "
                  >
                    <IdentifierLabel :label="targetSystem.name" :identifier="targetSystem.iuid" />
                  </Button>
                </div>
              </template>
            </div>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Target System Setup Parameters -->
        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('MedicalSystemParameter_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <ShowMore
              v-if="filterMenuEntries.targetSystemSetupParameters?.length"
              :collapsedHeight="290"
            >
              <v-container class="pa-0">
                <template
                  v-for="(setup, index) in filterMenuEntries.targetSystemSetupParameters"
                  :key="
                    instanceOfTextValueListDto(setup)
                      ? (setup as TextValueListDtoWithId).id
                      : setup.id
                  "
                >
                  <v-row dense>
                    <v-col cols="auto" class="d-flex align-center">
                      <IdentifierLabel
                        :label="getSetupParameterName(setup)"
                        :identifier="setup.iuid"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <!-- For Enumeration types -->
                      <div v-if="instanceOfEnumerationValueListDto(setup)" class="select-group">
                        <template v-for="option in setup.options">
                          <div
                            v-if="option.id"
                            :key="`enum-${setup.id}-${option.id}`"
                            class="select-item"
                          >
                            <Button
                              v-if="isOptionSelected(setup, option)"
                              variant="primary"
                              size="x-small"
                              iconPosition="start"
                              :icon="mdiCheckboxMarkedOutline"
                              @click="
                                () => {
                                  if (!option.id) return;
                                  toggleParameterOption(setup, option);
                                }
                              "
                            >
                              {{ $t(option.name as I18nKey) }}
                            </Button>
                            <Button
                              v-else
                              variant="secondary"
                              size="x-small"
                              iconPosition="start"
                              :icon="mdiCheckboxBlankOutline"
                              @click="
                                () => {
                                  if (!option.id) return;
                                  toggleParameterOption(setup, option);
                                }
                              "
                            >
                              {{ $t(option.name as I18nKey) }}
                            </Button>
                          </div>
                        </template>
                      </div>

                      <!-- For Discrete Numeric types -->
                      <div
                        v-else-if="instanceOfNumericDataSetFilterDto(setup)"
                        class="select-group"
                      >
                        <div
                          v-for="value in setup.availableValues"
                          :key="`numeric-${value.value}-${value.unitAbbreviation}`"
                          class="select-item"
                        >
                          <Button
                            v-if="isOptionSelected(setup, value)"
                            variant="primary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxMarkedOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value.value }} {{ value.unitAbbreviation }}
                          </Button>
                          <Button
                            v-else
                            variant="secondary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxBlankOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value.value }} {{ value.unitAbbreviation }}
                          </Button>
                        </div>
                      </div>

                      <!-- For Text types -->
                      <div v-else-if="instanceOfTextValueListDto(setup)" class="select-group">
                        <div
                          v-for="value in setup.availableValues"
                          :key="`text-${setup.dataTypeId}-${value}`"
                          class="select-item"
                        >
                          <Button
                            v-if="isOptionSelected(setup, value)"
                            variant="primary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxMarkedOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value }}
                          </Button>
                          <Button
                            v-else
                            variant="secondary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxBlankOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value }}
                          </Button>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <VDivider
                    v-if="index < filterMenuEntries.targetSystemSetupParameters.length - 1"
                    class="my-1"
                  />
                </template>
              </v-container>
            </ShowMore>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Support System Setup Parameters -->

        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('SupportSystemParameter_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <ShowMore
              v-if="filterMenuEntries.supportSystemSetupParameters?.length"
              :collapsedHeight="290"
            >
              <v-container class="pa-0">
                <template
                  v-for="(setup, index) in filterMenuEntries.supportSystemSetupParameters"
                  :key="setup.dataTypeId"
                >
                  <v-row dense>
                    <v-col cols="auto" class="d-flex align-center">
                      <IdentifierLabel
                        :label="getSetupParameterName(setup)"
                        :identifier="setup.iuid"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <!-- For Enumeration types -->
                      <div v-if="instanceOfEnumerationValueListDto(setup)" class="select-group">
                        <template
                          v-for="option in setup.options"
                          :key="`enum-${setup.id}-${option.id}`"
                        >
                          <div v-if="option.id" class="select-item">
                            <Button
                              v-if="isOptionSelected(setup, option)"
                              variant="primary"
                              size="x-small"
                              iconPosition="start"
                              :icon="mdiCheckboxMarkedOutline"
                              @click="
                                () => {
                                  if (!option.id) return;

                                  toggleParameterOption(setup, option);
                                }
                              "
                            >
                              {{ $t(option.name as I18nKey) }}
                            </Button>
                            <Button
                              v-else
                              variant="secondary"
                              size="x-small"
                              iconPosition="start"
                              :icon="mdiCheckboxBlankOutline"
                              @click="
                                () => {
                                  if (!option.id) return;

                                  toggleParameterOption(setup, option);
                                }
                              "
                            >
                              {{ $t(option.name as I18nKey) }}
                            </Button>
                          </div>
                        </template>
                      </div>

                      <!-- For Discrete Numeric types -->
                      <div
                        v-else-if="instanceOfNumericDataSetFilterDto(setup)"
                        class="select-group"
                      >
                        <div
                          v-for="value in setup.availableValues"
                          :key="`numeric-${setup.id}-${value}`"
                          class="select-item"
                        >
                          <Button
                            v-if="isOptionSelected(setup, value)"
                            variant="primary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxMarkedOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value.value }} {{ value.unitAbbreviation }}
                          </Button>
                          <Button
                            v-else
                            variant="secondary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxBlankOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value.value }} {{ value.unitAbbreviation }}
                          </Button>
                        </div>
                      </div>

                      <!-- For Text types -->
                      <div v-else-if="instanceOfTextValueListDto(setup)" class="select-group">
                        <div
                          v-for="value in setup.availableValues"
                          :key="`text-${setup.dataTypeId}-${value}`"
                          class="select-item"
                        >
                          <Button
                            v-if="isOptionSelected(setup, value)"
                            variant="primary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxMarkedOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value }}
                          </Button>
                          <Button
                            v-else
                            variant="secondary"
                            size="x-small"
                            iconPosition="start"
                            :icon="mdiCheckboxBlankOutline"
                            @click="
                              () => {
                                toggleParameterOption(setup, value);
                              }
                            "
                          >
                            {{ value }}
                          </Button>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <VDivider
                    v-if="index < filterMenuEntries.supportSystemSetupParameters.length - 1"
                    class="my-1"
                  />
                </template>
              </v-container>
            </ShowMore>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Evaluation Parameters -->
        <v-card class="content-block dense mt-3">
          <v-card-item>
            <v-card-title class="bg-primary-lighten-4 text-body-2">
              <h5 class="mb-0">{{ $t('Evaluation_other') }}</h5>
            </v-card-title>
          </v-card-item>

          <v-card-text>
            <div
              v-if="filterMenuEntries.evaluationParameters.length > 0"
              class="select-group evaluations"
            >
              <template
                v-for="evaluationParam in filterMenuEntries.evaluationParameters"
                :key="evaluationParam.value"
              >
                <div class="select-item">
                  <Button
                    v-if="isEvaluationParameterSelected(evaluationParam.id)"
                    variant="primary"
                    block
                    size="x-small"
                    iconPosition="start"
                    :icon="mdiCheckboxMarkedOutline"
                    @click="
                      () => {
                        deselectEvaluationParameter(evaluationParam.id);
                      }
                    "
                  >
                    <IdentifierLabel
                      :label="getSetupParameterName(evaluationParam)"
                      :identifier="evaluationParam.iuid"
                    />
                  </Button>
                  <Button
                    v-else
                    variant="secondary"
                    size="x-small"
                    block
                    iconPosition="start"
                    :icon="mdiCheckboxBlankOutline"
                    @click="
                      () => {
                        selectEvaluationParameter(evaluationParam.id);
                      }
                    "
                  >
                    <IdentifierLabel
                      :label="getSetupParameterName(evaluationParam)"
                      :identifier="evaluationParam.iuid"
                    />
                  </Button>
                </div>
              </template>
            </div>
            <p v-else class="text-center">
              {{ $t('NoResultsFound') }}
            </p>
          </v-card-text>
        </v-card>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';
@use '@/styles/mixins';
@use '@/styles/vuetifySettings';

#filter-controls {
  width: 100%;
  display: flex;
  gap: 1rem;

  .input {
    width: 66%;
  }

  .right {
    display: flex;
    gap: 1rem;
    width: 50%;
    justify-content: flex-end;

    > div {
      width: 50%;
    }
  }
}

body > .v-overlay-container {
  #filter-menu.v-menu {
    .v-list {
      padding: 0;
      .v-list-subheader {
        justify-content: center;
        font-size: variables.$body-font-size;
        font-weight: variables.$fontweight_medium;
        color: var(--v-theme-on-surface);

        @include mixins.shadow-bottom(3);
      }
      .v-list-item {
        pointer-events: none;

        :deep(.v-list-item__content) {
          padding: vuetifySettings.$spacer;
        }
      }
    }
    :deep(.v-overlay__content) {
      .v-btn {
        pointer-events: auto;

        &.v-btn--block {
          justify-content: start;
          .v-btn__content {
            margin: 0 auto;
          }
        }
      }
    }
  }
}

.select-group {
  display: flex;
  gap: vuetifySettings.$spacer;
  flex-wrap: wrap;

  .select-item {
    :deep(.v-btn) {
      width: auto;
    }
    :deep(.v-btn__prepend) {
      padding-left: 3px;
    }
    :deep(.v-btn__content) {
      padding-left: 2px;
      padding-right: 3px;
    }
  }
}
.collapsed {
  --content-block-collapsed-height: 300px;
}
:deep(.v-overlay__scrim) {
  background-color: transparent !important;
}
</style>
